import React, { useMemo, useState} from "react";
import {Link, Route, Routes, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useDebounce} from "react-use";
import TalentProvider from "../../providers/TalentProvider";
import {Empty, Input, Select} from "antd";
import TablePagination from "../../components/TablePagination";
import {useTalentPool} from "../../providers/TalentPoolProvider";
import {useAPI} from "../../config/API";
import SidebarComponent from "../../components/SidebarComponent";
import SearchTalentSidebar from "../talent/SearchTalentSidebar";
import SpinLoader from "../../components/SpinLoader";
import {useIsMutating} from "@tanstack/react-query";
import BulkAddTalentContainer from "./BulkAddTalentContainer";
import LoadingBanner from "../../components/LoadingBanner";
import {TalentProfile} from "../profiles/TalentProfilePage";
import UserDiscipline from "../../models/UserDiscipline";
import PageNotFound from "../../components/errors/PageNotFound";
import ComponentError from "../../components/errors/ComponentError";
import EmptyFeedback from "../../components/EmptyFeedback";
import TalentTileRedesign, { TalentTileRedesignSkeleton } from "../talent/TalentTileRedesign";

const MAX_BULK_ACTIONS = 15;
const TalentPoolAdd = ({ searchData, error, refetch, isSearchLoading, isInitialLoading, hasSetRequiredFilters, criteriaForMatching }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [inputValue, setInputValue] = useState(searchParams.get('search') || '');
    const [bulkActions, setBulkActions] = useState([]);
    const { talentPoolId } = useParams();
    const navigate = useNavigate();

    const isAddingOrRemovingMutation = useIsMutating({
        predicate: mutation => {
            return mutation.key === 'bulkAddTalentToPoolMutation' || mutation.key === 'addTalentToPoolMutation' || mutation.key === 'removeTalentFromPoolMutation'
        }
    })
    const {
        isLoading,
        talentPool,
        onUpdateTalent,
        onBulkAddTalentToPool,
        onAddTalentToPool,
        onRemoveTalentFromPool,
        isDisabled,
        onChangeSort,
        SORT_OPTIONS_TALENT,
        isAddingOrRemovingTalent,
    } = useTalentPool();

    const onRunBulkActions = async () => {
        // for (let talent of bulkActions) {
        //     let added = await onBulkAddTalentToPool(talent.id, false);
        //     if (!added) {
        //         break;
        //     }
        // }
        let updated = await onBulkAddTalentToPool(TB.map(bulkActions, 'id'), false);
        if (updated) {
            await setBulkActions([]);

            // update count for current talent pool and reset search
            // queryClient.setQueryData(['fetchTalentPools', auth.clientOrg?.()?.id], oldData => {
            //     return oldData.map(pool => {
            //         if (pool.id === Number(talentPoolId)) {
            //             return {
            //                 ...pool,
            //                 talent_users_count: pool.talent_users_count + bulkActions.length,
            //             }
            //         }
            //         return pool
            //     })
            // })
            //
            // await queryClient.invalidateQueries(['fetchTalentPoolDetail'])
            // await queryClient.invalidateQueries(['searchForTalent'])

        }
        // return navigate(`/client/talent-pool/${talentPoolId}`, { replace: true })
    }
    const onAddToBulkActions = (talent) => {

        let updatedActions = TB.xorBy([...bulkActions], [talent], 'id');
        if (updatedActions?.length >= MAX_BULK_ACTIONS) {
            return TB.toast(`Max of ${MAX_BULK_ACTIONS} candidates can be selected at a time`, { theme: 'danger' });
        }
        setBulkActions(updatedActions)
    }




    const [, cancel] = useDebounce(
        () => {
            const params = new URLSearchParams(searchParams);
            if (inputValue.length) {
                params.set('search', inputValue);
            } else {
                params.delete('search');
            }
            return setSearchParams(params, { replace: true, preventScrollReset: true });
        }, 800, [inputValue]
    );

    // console.log('TalentPoolAddisAddingOrRemovingMutation', isAddingOrRemovingMutation > 0)
    const defaultBrowseFilters = useMemo(() => {
        return talentPool?.defaultSearchParams();
    }, [isSearchLoading, searchParams?.toString(), TB.storedBrowseFilters('talentPool-' + talentPoolId)]);

    return (
        <div className="w-full">

            <div className="bg-black p-4 flex justify-between gap-8 flex-wrap">
                <div className="flex flex-col">
                    <p className="text-sm text-gray-400">Add Talent</p>
                    <p className="text-xl text-white font-bold my-auto">{talentPool?.name}</p>
                </div>

                <div className="bg-wrippleSuccessDarker w-[134px] text-center p-2.5 flex flex-col justify-center">
                    <p className="text-2xl text-white">{talentPool?.talent_users_count}</p>
                    <p className="text-sm text-white">Already In Pool</p>
                </div>

                <div className="bg-white w-[134px] text-center p-2.5 flex flex-col justify-center">
                    <p className="text-2xl">{bulkActions?.length || 0}</p>
                    <p className="text-sm">Being Added</p>
                </div>

                <div className="pl-4">
                    <ol className="text-xs text-white list-decimal mb-0 h-full flex flex-col justify-between">
                        <li>Use the search filters to search for talent</li>
                        <li>Roll-over talent and check the box to select talent you want to add</li>
                        <li>Click the <span className="font-bold">Add To Pool</span> to add all selected talent to pool</li>
                        <li>Repeat or click the  <span className="font-bold">Go Back</span> button</li>
                    </ol>
                </div>

                <div>
                    <p className="text-white pb-1">Done adding for now?</p>

                    <Link to={`/client/talent-pool/${talentPoolId}`} className="btn btn-primary text-base">
                        Go Back
                    </Link>
                </div>
            </div>

            <div className="flex h-full">
                <SidebarComponent canClose={true}>
                    <SearchTalentSidebar
                        title="Talent Search"
                        enableImportance={true} enableExperience={true}
                        disabledFilters={["TALENT_TYPES"]}
                        useLocalStorage={'talentPool-' + talentPoolId}
                        openOnMount={true}
                        customFilters={defaultBrowseFilters.custom}
                        defaultFilters={defaultBrowseFilters || {}}
                    >
                        <div className="my-2" />
                    </SearchTalentSidebar>
                </SidebarComponent>

                <div id="talent-pool-add-container" className="relative flex-1">
                    <SpinLoader isLoading={isAddingOrRemovingTalent} className="flex flex-col py-12 pl-14 pr-10">
                        <div className="talent-pool-add flex flex-1 flex-grow relative" >
                            <div className="flex flex-grow">
                                {!error ? (
                                    searchParams.toString()?.length > 0 ? (
                                        <div className="flex-grow flex flex-col items-stretch">

                                            {hasSetRequiredFilters && (
                                                <React.Fragment>
                                                    <Input
                                                        size="large"
                                                        maxLength={40}
                                                        className="w-full p-3"
                                                        placeholder="Search By Name"
                                                        value={inputValue}
                                                        disabled={isInitialLoading || searchData?.users?.length === 0}
                                                        onChange={({ currentTarget }) => setInputValue(currentTarget.value)}
                                                    />
                                                    <div className="flex gap-4 justify-between items-center my-4">
                                                        <p className="text-black font-bold text-lg">
                                                            Choose talent to add to your pool
                                                        </p>

                                                        <div className="flex flex-row gap-1 items-center">
                                                            <p>Sort:</p>
                                                            <Select
                                                                defaultValue="best_match"
                                                                value={SORT_OPTIONS_TALENT[searchParams.get('sort_by') || 'best_match']}
                                                                onChange={(key) => onChangeSort(key)}
                                                                bordered={false}
                                                                options={TB.map(SORT_OPTIONS_TALENT, (label, value) => ({
                                                                    value,
                                                                    label
                                                                }))}
                                                                disabled={isInitialLoading}
                                                                popupClassName="min-w-[275px]"
                                                                getPopupContainer={() => document.getElementById('talent-pool-add-container')}
                                                                className="text-primary"
                                                                placement="bottomRight"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="pb-3">
                                                        {searchData && !isInitialLoading && (
                                                            <TablePagination
                                                                isDisabled={isSearchLoading || isInitialLoading}
                                                                currentPage={searchData.current_page}
                                                                useParams
                                                                pagesCount={searchData.last_page}
                                                                total={searchData.total}
                                                                perPage={searchData.per_page}
                                                            />
                                                        )}
                                                    </div>

                                                </React.Fragment>
                                            )}


                                            {hasSetRequiredFilters ? (
                                                <React.Fragment>

                                                    <div className="pt-6 flex-grow">
                                                        <div className="browse-list full-card-list justify-center">
                                                            {(!searchData || isSearchLoading) ? (
                                                                <div className="flex flex-wrap gap-8 justify-center">
                                                                    {TB.map([...Array(12).keys()], index => (
                                                                        <div className="max-w-[342px] min-w-[342px]" key={`placeholder-${index}`}>
                                                                            <TalentTileRedesignSkeleton />
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                searchData?.users?.length === 0 ? (
                                                                    <EmptyFeedback>
                                                                        <div className="flex flex-col items-center">
                                                                            <p className="text-sm font-medium text-center pb-6">
                                                                                No talent found within your search filters
                                                                            </p>

                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-primary"
                                                                                onClick={() => {
                                                                                    navigate({ pathname: '.', search: 'reset=1' }, { replace: true })
                                                                                }}
                                                                            >
                                                                                Reset Search
                                                                            </button>
                                                                        </div>
                                                                    </EmptyFeedback>
                                                                ) : (
                                                                    <div
                                                                        className="flex flex-wrap gap-8 justify-center">
                                                                        {searchData?.users.map(user => (
                                                                            <div key={`tal-${user.id}`}
                                                                                className="max-w-[342px] min-w-[342px]">
                                                                                <TalentProvider
                                                                                    talent={user}
                                                                                    onUpdated={updatedTalent => {
                                                                                        console.log('onUpdated', updatedTalent)
                                                                                        onUpdateTalent(updatedTalent)
                                                                                    }}
                                                                                    isDisabled={isDisabled}
                                                                                    onRequest={(requestCallback) => requestCallback()}
                                                                                    criteria={criteriaForMatching}
                                                                                    canFavorite={false}
                                                                                >
                                                                                    <TalentTileRedesign
                                                                                        hideAvailability={true}
                                                                                        includeRating={false}
                                                                                        onRequest={(requestCallback) => requestCallback()}
                                                                                        onUpdated={updatedTalent => {
                                                                                            console.log('onUpdated', updatedTalent)
                                                                                            onUpdateTalent(updatedTalent)
                                                                                        }}
                                                                                        bulkActions={bulkActions}
                                                                                        isDisabled={isLoading || isDisabled}
                                                                                        placeholder={isSearchLoading}
                                                                                        isBulkActionsSelected={TB.find(bulkActions, [
                                                                                            'id',
                                                                                            user.id
                                                                                        ])}
                                                                                        canRunBulkActions={!user.is_in_talent_pool}
                                                                                        onAddToBulkActions={() => onAddToBulkActions(user)}
                                                                                        onRemoveFromPool={user.is_in_talent_pool && (() => onRemoveTalentFromPool(user))}
                                                                                    >
                                                                                        <div
                                                                                            className="h-[18px] bg-white">

                                                                                        </div>
                                                                                    </TalentTileRedesign>
                                                                                </TalentProvider>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    </div>

                                                    <div className="pt-8">
                                                        {searchData?.last_page > 1 && (
                                                            <TablePagination
                                                                isDisabled={isSearchLoading}
                                                                currentPage={searchData.current_page}
                                                                useParams
                                                                pagesCount={searchData.last_page}
                                                                total={searchData.total}
                                                                perPage={searchData.per_page}
                                                            />
                                                        )}
                                                    </div>
                                                </React.Fragment>
                                            ) : (
                                                <EmptyFeedback>
                                                    <div className="flex flex-col items-center">
                                                        <p className="text-sm font-medium text-center pb-6">
                                                            Select at least one role to search talent
                                                        </p>

                                                        <button
                                                            type="button"
                                                            className="btn btn-primary"
                                                            onClick={() => {
                                                                navigate({ pathname: '.', search: 'reset=1' }, { replace: true })
                                                            }}
                                                        >
                                                            Reset Search
                                                        </button>
                                                    </div>
                                                </EmptyFeedback>
                                            )}
                                        </div>
                                    ) : (
                                        <div
                                            className="flex flex-col self-stretch flex-grow items-center justify-center mx-auto">
                                            <Empty description="Start searching to add talent"
                                                image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                        </div>
                                    )
                                ) : (
                                    <ComponentError>
                                        <button
                                            type="button"
                                            className="btn btn-primary mt-2"
                                            onClick={() => {
                                                navigate({ pathname: '.', search: 'reset=1' }, { replace: true })
                                            }}
                                        >
                                            Reset Search
                                        </button>
                                    </ComponentError>
                                )}

                            </div>
                        </div>
                    </SpinLoader>
                </div>

                <BulkAddTalentContainer
                    bulkActions={bulkActions}
                    onClearBulkActions={() => setBulkActions([])}
                    onRunBulkActions={onRunBulkActions} />
            </div>
        </div>
    )
}


const disciplineCategories = TB.map([...laravel.discipline_categories], cat => {
    let option = TB.pick(cat, ['id', 'name', 'disciplines', 'display_order'])
    let disciplines = option.disciplines;//TB.filter(option.disciplines, ['is_contractable', true]);

    option.disciplines = TB.map(disciplines, discipline => TB.pick(discipline, ['id', 'name', 'display_order']))
    option.value = option.id;

    return option
});

// console.log(disciplineCategories, 'DISPLICE CATS')

const disciplines = TB.map(disciplineCategories, category => {
    return {
        ...category,
        options: category.disciplines.map(discipline => {
            return {
                ...discipline,
                value: discipline.id
            }
        })
    }
})
const industries = TB.map(laravel.client_industries, (name, value) => {
    return { name, value: Number(value), id: Number(value) }
});

const TalentPoolAddLayout = (props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { talentPoolId } = useParams();

    const {
        isLoading,
        talentPool,
        SORT_OPTIONS_TALENT
    } = useTalentPool();

    //
    // CRUD TALENT POOL
    //
    const decodedSearchParams = useMemo(() => {
        let decoded = [...searchParams.entries()].reduce((acc, [key, val]) => {
            return { ...acc, [key]: val };
        }, {});
        if (!decoded['sort_by']) {
            decoded['sort_by'] = TB.first(TB.keys(SORT_OPTIONS_TALENT))
        }
        return decoded
    }, [searchParams?.toString()]);


    const hasSetRequiredFilters = useMemo(() => {

        let selectedDisciplines = TB.flatMap(
            TB.map(TB.filter(decodedSearchParams, (value, key) => key.includes('disciplines')), dis => {
                try {
                    return JSON.parse(dis)
                } catch {
                    return dis
                }
            })
        )
        return selectedDisciplines?.length > 0;
    }, [decodedSearchParams]);

    const {
        data: searchData,
        error,
        refetch,
        isFetching: isSearchLoading,
        isInitialLoading
    } = useAPI([
        // 'searchForTalent', { ...decodedSearchParams, is_not_in_pool_id: talentPoolId }
        'searchForTalent', {
            ...decodedSearchParams,
            is_not_in_pool_id: TB.isInteger(parseInt(talentPoolId)) ? talentPoolId : undefined,
            local_storage: TB.storedBrowseFilters('talentPool-' + talentPoolId)
    }
    ], {
        enabled: !!talentPool && !isLoading && searchParams.toString()?.length > 0 && hasSetRequiredFilters,
        keepPreviousData: true
    });

    const criteriaForMatching = useMemo(() => {
        const currentParams = Object.fromEntries([...searchParams]);
        let customFilters = {};
        if (currentParams['custom']) {
            try {
                customFilters = currentParams['custom'] ? JSON.parse(currentParams['custom']) : []
            }catch (e) {
                console.warn(e)
            }
        }
        if (!Object.keys(currentParams)?.length) return null;

        let criteria = {};

        try {
            TB.map(currentParams, (value, key) => {

                if (key.includes('disciplines')) {
                    let roleIds = JSON.parse(value) || [];

                    roleIds = TB.filter(roleIds, role => !TB.isNaN(parseInt(role)))
                    let roles = TB.filter(TB.flatMap(disciplines, 'options'), dis => roleIds.includes(dis.id));
                    criteria.disciplines = TB.uniqBy([
                        ...(criteria.disciplines || []),
                        ...roles
                    ], 'id');

                    // let scorePrefix = TB.replace(key, '_disciplines', '');
                    // if (scorePrefix && TB.find(experienceLevels, ['prefix', scorePrefix])) {
                    //     criteria.min_score = TB.find(experienceLevels, ['prefix', scorePrefix])?.value;
                    // }
                }
                if (key === 'experience') {
                    try {
                        let experience = JSON.parse(value);
                        TB.map(experience, (isChecked, expType) => {
                            criteria[expType] = isChecked;
                        })
                    } catch (e) {
                        console.warn('jsonError', e)
                    }
                }
                if (key.includes('skills')) {
                    let skillIds = JSON.parse(value) || [];
                    skillIds = TB.filter(skillIds, skill => !TB.isNaN(parseInt(skill)))
                    const skillsByRole = UserDiscipline.skillsForDisciplines(TB.map(TB.flatMap(disciplines, 'options'), 'id')) || [];
                    const customSkills = TB.isArray(customFilters?.skills) ? customFilters?.skills : [];
                    let skills = TB.filter([...(skillsByRole || []), ...(customSkills || [])], skill => skillIds.includes(skill.id));
                    let scorePrefix = TB.replace(key, 'skills', '');

                    criteria[`${scorePrefix}skills`] = TB.uniqBy([
                        ...(criteria[`${scorePrefix}skills`] || []),
                        ...skills
                    ], 'id')
                } else if (key.includes('industries')) {
                    let industriesIds = JSON.parse(value) || [];

                    industriesIds = TB.filter(industriesIds, industry => !TB.isNaN(parseInt(industry)))
                    const customIndustries = TB.isArray(customFilters?.industries) ? customFilters?.industries : [];
                    let industriesSearch = TB.filter([...industries, ...(customIndustries || [])], industry => industriesIds.includes(industry.id));
                    let scorePrefix = TB.replace(key, 'industries', '');

                    criteria[`${scorePrefix}industries`] = TB.uniqBy([
                        ...(criteria[`${scorePrefix}industries`] || []),
                        ...industriesSearch
                    ], 'id')
                }


                if (key === 'hired') {
                    criteria.hired = JSON.parse(value)
                }

                if (key === 'favorited') {
                    criteria.favorited = JSON.parse(value)
                }
                if (key === 'invited') {
                    criteria.invited = JSON.parse(value)
                }
                if (key === 'pool_ids') {
                    criteria.pool_ids = JSON.parse(value)

                    criteria.pool_ids = TB.filter(criteria.pool_ids, pool => !TB.isNaN(parseInt(pool)))
                }
                if (key === 'brands') {
                    criteria.brands = JSON.parse(value)

                    criteria.brands = TB.filter(criteria.brands, brandID => !TB.isNaN(parseInt(brandID)))
                }
                if (key === 'location' && !TB.isNaN(parseInt(JSON.parse(value)))) {
                    criteria.location = parseInt(JSON.parse(value))
                }
                if (key === 'address') {
                    criteria.address = value
                }
                if (key === 'regions') {
                    criteria.regions = JSON.parse(value)

                    criteria.regions = TB.filter(criteria.regions, regionId => !TB.isNaN(parseInt(regionId)))
                }
                if (key === 'latitude' && !TB.isNaN(parseFloat(JSON.parse(value)))) {
                    criteria.latitude = parseFloat(JSON.parse(value))
                }
                if (key === 'longitude' && !TB.isNaN(parseFloat(JSON.parse(value)))) {
                    criteria.longitude = parseFloat(JSON.parse(value))
                }
            })
        }catch (e) {
            console.error("JSON Parse Error", e)
            TB.persistStoredBrowseFilters('talentPool-' + talentPoolId, {});
        }

        return criteria;
    }, [searchParams?.toString()]);

    return (
        <Routes>
            <Route path={"*"}>
                <Route index element={(
                    isLoading ?
                        (
                            <div className="w-full flex flex-col items-center justify-center">
                                <LoadingBanner />
                            </div>
                        ) :
                        <TalentPoolAdd
                            refetch={refetch}
                            searchData={searchData}
                            isSearchLoading={isSearchLoading}
                            criteriaForMatching={criteriaForMatching}
                            isInitialLoading={isInitialLoading}
                            error={error}
                            hasSetRequiredFilters={hasSetRequiredFilters} />
                )} />
                <Route path={'talent-profiles/:profileId/*'} element={
                    (isSearchLoading || isLoading) ?
                        (
                            <div className="flex flex-col items-center justify-center">
                                <LoadingBanner />
                            </div>
                        ) :
                        <TalentProfile criteria={criteriaForMatching} browseResults={searchData?.users} />
                } />
                <Route path="*" element={<PageNotFound />} />

            </Route>
        </Routes>
    )
};

export default TalentPoolAddLayout;